import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import MarketplaceHeader from '../../components/navigation/MarketplaceHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { trackEvent } from '../../hooks/useGoogleAnalytics';

const AboutEbasVenture = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackEvent('page_view', {
      page_title: 'About OBAS Private Limited',
      page_path: '/about-ebas-venture'
    });
  }, []);

  const companyStats = [
  { label: 'Services Delivered', value: '10,000+', icon: 'CheckCircle' },
  { label: 'Global Partners', value: '500+', icon: 'Users' },
  { label: 'Customer Satisfaction', value: '98%', icon: 'Star' },
  { label: 'Countries Served', value: '25+', icon: 'Globe' }];


  const coreValues = [
  {
    icon: 'Target',
    title: 'Excellence',
    description: 'Committed to delivering exceptional quality to our international clients across UK, US, Canada, UAE, and beyond'
  },
  {
    icon: 'Shield',
    title: 'Trust',
    description: 'Building lasting relationships with global businesses through transparency, reliability, and consistent cross-border service delivery'
  },
  {
    icon: 'Zap',
    title: 'Innovation',
    description: 'Leveraging cutting-edge technology to streamline international service delivery and enhance customer experience worldwide'
  },
  {
    icon: 'Globe',
    title: 'Global Reach',
    description: 'Connecting businesses in UK, US, Canada, UAE, and India with verified professionals through our OTAssist platform'
  }];


  const milestones = [
  {
    year: '2019',
    title: 'The Beginning',
    description: "OBASE Group's journey began with the establishment of EUCIT Systems Private Limited, founded with a vision to deliver reliable and scalable technology services across PAN India. From the outset, the focus was on empowering channel partners, B2B clients, and enterprises with strong IT capabilities."
  },
  {
    year: '2022',
    title: 'Nationwide Growth & Industry Impact',
    description: 'By 2022, EUCIT successfully expanded its presence across India, building a robust network of 35+ channel partners and serving 50+ large enterprise customers. The organization delivered impactful projects across multiple sectors, offering services in IT Infrastructure, Cybersecurity, Application Development, and Large-scale Project Deployments. This phase marked EUCIT as a trusted technology partner with proven execution capabilities.'
  },
  {
    year: '2024',
    title: 'Expansion Beyond IT',
    description: "Building on EUCIT's success, the group expanded into non-IT domains with the launch of EBAS Venture Private Limited. This new venture focused on Business Operations Outsourcing (BPO) and Resource Augmentation. The goal was to support enterprises with operational scalability and workforce efficiency beyond technology services."
  },
  {
    year: '2025',
    title: 'Strengthening Lifestyle & Concierge Services',
    description: 'In 2025, OBASE Group acquired PICKPA Services Private Limited, a 4-year-old company specializing in tasker-based concierge services. This strategic move enabled the group to provide personal assistant services, simplify daily life operations, and help individuals and families enjoy a stress-free, convenient lifestyle.'
  },
  {
    year: '2026',
    title: 'Launch of ZippyAssist',
    description: 'Continuing its innovation journey, OBASE Group introduced the ZippyAssist brand—extending personal assistant services into the business ecosystem. ZippyAssist empowers organizations to address operational gaps quickly, access on-demand support services, and ensure faster resolutions with priority handling.'
  },
  {
    year: '2027',
    title: 'The Future Vision',
    description: 'Looking ahead, OBASE Group aims to establish PAN India and global skill-based service delivery centers. These centers will ensure seamless service execution, scalable global operations, and a strong commitment to 99% SLA guarantee. This journey reflects OBASE Group\'s evolution from a technology-focused company to a multi-service global ecosystem, committed to delivering excellence across industries and geographies.'
  }];


  const certifications = [
  { name: 'ISO 9001:2015', description: 'Quality Management System' },
  { name: 'ISO 27001', description: 'Information Security Management' },
  { name: 'GDPR Compliant', description: 'Data Protection Standards' },
  { name: 'SOC 2 Type II', description: 'Security & Availability' }];


  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>About ZippyAssist | EBAS Venture Private Limited - Global Service Marketplace</title>
        <meta name="description" content="ZippyAssist is operated by EBAS Venture Private Limited under the OBASEGroup. Connecting global businesses in UK, US, Canada & UAE with 500+ verified Indian service partners since 2019." />
        <meta name="keywords" content="ZippyAssist, EBAS Venture, OBASEGroup, EUCIT Systems, PickPA Service, business services marketplace, global outsourcing, India service partners" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://zippyassist.in/about-ebas-venture" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="About ZippyAssist | EBAS Venture Private Limited" />
        <meta property="og:description" content="ZippyAssist by EBAS Venture Private Limited — connecting global businesses with 500+ verified Indian service partners across 2000+ categories." />
        <meta property="og:url" content="https://zippyassist.in/about-ebas-venture" />
        <meta property="og:site_name" content="ZippyAssist" />
        <meta property="og:image" content="https://zippyassist.in/assets/images/200x68-1775706855169.jpg" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About ZippyAssist | EBAS Venture Private Limited" />
        <meta name="twitter:description" content="ZippyAssist by EBAS Venture Private Limited — 500+ verified partners, 25+ countries, 2000+ business services." />
        <meta name="twitter:image" content="https://zippyassist.in/assets/images/200x68-1775706855169.jpg" />

        {/* JSON-LD: Organization */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "EBAS Venture Private Limited",
          "alternateName": ["ZippyAssist", "OBASEGroup"],
          "url": "https://zippyassist.in",
          "logo": {
            "@type": "ImageObject",
            "url": "https://zippyassist.in/assets/images/200x68-1775706855169.jpg"
          },
          "description": "EBAS Venture Private Limited operates ZippyAssist, a global marketplace connecting international businesses with verified Indian service partners across 2000+ intangible service categories.",
          "foundingDate": "2019",
          "numberOfEmployees": { "@type": "QuantitativeValue", "value": 50 },
          "areaServed": ["India", "United Kingdom", "United States", "Canada", "United Arab Emirates"],
          "contactPoint": [
            {
              "@type": "ContactPoint",
              "telephone": "+91-7039149001",
              "contactType": "customer service",
              "areaServed": ["IN", "GB", "US", "CA", "AE"],
              "availableLanguage": "English"
            }
          ],
          "email": "business@obasegroup.com",
          "sameAs": ["https://www.obasegroup.com"],
          "subOrganization": [
            { "@type": "Organization", "name": "EUCIT Systems Private Limited" },
            { "@type": "Organization", "name": "PickPA Service Private Limited" }
          ]
        })}</script>
      </Helmet>
      <MarketplaceHeader />
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary to-primary/80 text-primary-foreground py-16 md:py-24 lg:py-32 px-4 md:px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          </div>
          <div className="max-w-5xl mx-auto text-center relative z-10">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Icon name="Building2" size={20} />
              <span className="text-sm font-bold">OBASE Group  Of  Companies </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6">
              Empowering Global Businesses Across UK, US, Canada, UAE & India
            </h1>
            <p className="text-lg md:text-xl opacity-90 max-w-3xl mx-auto mb-8">We are OBAS Group, the multi-service global ecosystem behind ZippyAssist - a revolutionary platform trusted by international businesses across UK 🇬🇧, US 🇺🇸, Canada 🇨🇦, UAE 🇦🇪, and India 🇮🇳 with verified service professionals across 2000+ categories.

            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild>
                <Link to="/service-catalog-home">
                  <Icon name="ShoppingBag" size={20} className="mr-2" />
                  Explore Services
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-white/10" asChild>
                <a href="https://ebas.in" target="_blank" rel="noopener noreferrer">
                  <Icon name="ExternalLink" size={20} className="mr-2" />
                  Visit EBAS.in
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Company Stats */}
        <section className="py-12 md:py-16 px-4 md:px-6 lg:px-8 bg-muted">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {companyStats?.map((stat, index) =>
              <div key={index} className="bg-card rounded-xl p-6 text-center border border-border hover:shadow-lg transition-shadow">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4">
                    <Icon name={stat?.icon} size={24} className="text-primary" />
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-foreground mb-2">{stat?.value}</div>
                  <div className="text-sm text-muted-foreground">{stat?.label}</div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Company Story */}
        <section className="py-16 md:py-24 px-4 md:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Our Story</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                From a vision to transform service delivery to becoming a global platform trusted by thousands
              </p>
            </div>
            <div className="bg-card rounded-2xl p-8 md:p-12 border border-border space-y-8">

              {/* Intro */}
              <div>
                <p className="text-base md:text-lg text-foreground leading-relaxed">
                  OBASE Group is a global, multi-service organization built on a vision to simplify and strengthen business operations through an integrated Business Services Mall approach. We act as your global extended operations partner, delivering seamless, scalable, and reliable services across industries and geographies.
                </p>
              </div>

              {/* Three Pillars */}
              <div>
                <p className="text-base md:text-lg text-foreground leading-relaxed mb-4">
                  At the core of OBASE Group are three specialized pillars:
                </p>
                <ul className="space-y-3">
                  <li className="flex gap-3 text-base md:text-lg text-foreground leading-relaxed">
                    <span className="mt-1 flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-2"></span>
                    <span><strong className="text-foreground">EUCIT Systems Private Limited</strong> – Driving innovation in IT Infrastructure and Cybersecurity, ensuring secure, resilient, and future-ready digital environments.</span>
                  </li>
                  <li className="flex gap-3 text-base md:text-lg text-foreground leading-relaxed">
                    <span className="mt-1 flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-2"></span>
                    <span><strong className="text-foreground">EBAS Venture Private Limited</strong> – Enabling businesses through Business Operations Outsourcing and Resource Augmentation, helping organizations scale efficiently with the right talent and processes.</span>
                  </li>
                  <li className="flex gap-3 text-base md:text-lg text-foreground leading-relaxed">
                    <span className="mt-1 flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-2"></span>
                    <span><strong className="text-foreground">PICKPA Services Private Limited</strong> – Redefining convenience with personal assistant and tasker-based services, bridging everyday operational gaps for individuals and businesses alike.</span>
                  </li>
                </ul>
                <p className="text-base md:text-lg text-foreground leading-relaxed mt-4">
                  Together, these entities form a unified ecosystem designed to deliver end-to-end support—whether it's technology, operations, or on-ground assistance.
                </p>
              </div>

              {/* Our Vision */}
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3">Our Vision</h3>
                <p className="text-base md:text-lg text-foreground leading-relaxed">
                  To build a globally connected Business Services Mall that empowers organizations with multi-domain service delivery under one trusted umbrella—making OBASE Group the preferred global extended operations partner.
                </p>
              </div>

              {/* Global Reach */}
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3">Global Reach &amp; Capability</h3>
                <p className="text-base md:text-lg text-foreground leading-relaxed mb-4">
                  Today, OBASE Group operates internationally with a strong presence across:
                </p>
                <ul className="space-y-2 mb-4">
                  {["United Kingdom", "United States", "Canada", "United Arab Emirates"]?.map((country) =>
                  <li key={country} className="flex items-center gap-3 text-base md:text-lg text-foreground">
                      <span className="flex-shrink-0 w-2 h-2 rounded-full bg-primary"></span>
                      {country}
                    </li>
                  )}
                </ul>
                <p className="text-base md:text-lg text-foreground leading-relaxed mb-2">
                  With expansion underway in 20+ additional countries, we are rapidly strengthening our global footprint.
                </p>
                <p className="text-base md:text-lg text-foreground leading-relaxed">
                  Our network includes <strong>500+ verified business associates and channel partners</strong>, enabling us to provide localized expertise with global standards.
                </p>
              </div>

              {/* What Sets Us Apart */}
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3">What Sets Us Apart</h3>
                <ul className="space-y-2">
                  {[
                  "Integrated multi-service delivery model",
                  "Cross-border operational support for international businesses",
                  "Strong partner ecosystem ensuring reliability and scalability",
                  "Technology-driven approach backed by global expertise",
                  "Commitment to quality, compliance, and client success"]?.
                  map((item) =>
                  <li key={item} className="flex items-start gap-3 text-base md:text-lg text-foreground leading-relaxed">
                      <span className="flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-2"></span>
                      {item}
                    </li>
                  )}
                </ul>
              </div>

              {/* Our Commitment */}
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3">Our Commitment</h3>
                <p className="text-base md:text-lg text-foreground leading-relaxed">
                  At OBASE Group, we are committed to delivering excellence through innovation, trust, and operational efficiency. We specialize in supporting overseas businesses looking to establish, manage, or expand their operations in India and beyond—ensuring smooth, compliant, and cost-effective execution.
                </p>
              </div>

            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-muted">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Our Journey</h2>
              <p className="text-lg text-muted-foreground">Key milestones in our growth story</p>
            </div>
            <div className="space-y-8">
              {milestones?.map((milestone, index) =>
              <div key={index} className="flex gap-6 md:gap-8">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold">
                      {milestone?.year}
                    </div>
                  </div>
                  <div className="flex-1 bg-card rounded-xl p-6 border border-border">
                    <h3 className="text-xl font-semibold text-foreground mb-2">{milestone?.title}</h3>
                    <p className="text-muted-foreground">{milestone?.description}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-16 md:py-24 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Our Core Values</h2>
              <p className="text-lg text-muted-foreground">The principles that guide everything we do</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {coreValues?.map((value, index) =>
              <div key={index} className="bg-card rounded-xl p-6 border border-border hover:shadow-lg transition-all hover:-translate-y-1">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-4">
                    <Icon name={value?.icon} size={28} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">{value?.title}</h3>
                  <p className="text-muted-foreground">{value?.description}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* OTAssist Platform */}
        <section className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-gradient-to-br from-primary/5 to-primary/10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
                <Icon name="Zap" size={20} className="text-primary" />
                <span className="text-sm font-medium text-primary">Our Technology</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">The OTAssist Platform</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                A comprehensive service marketplace built with cutting-edge technology to ensure seamless global service delivery
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-card rounded-xl p-6 border border-border">
                <Icon name="Search" size={32} className="text-primary mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Smart Discovery</h3>
                <p className="text-sm text-muted-foreground">AI-powered search across 2000+ service categories to find exactly what you need</p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border">
                <Icon name="ShieldCheck" size={32} className="text-primary mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Verified Partners</h3>
                <p className="text-sm text-muted-foreground">Rigorous verification process ensuring quality and reliability of all service providers</p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border">
                <Icon name="Clock" size={32} className="text-primary mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Real-time Updates</h3>
                <p className="text-sm text-muted-foreground">Instant notifications and live tracking of your service requests and deliveries</p>
              </div>
            </div>
            <div className="bg-card rounded-2xl p-8 md:p-12 border border-border">
              <h3 className="text-2xl font-bold text-foreground mb-6">Platform Capabilities</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex gap-4">
                  <Icon name="CheckCircle" size={24} className="text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Global Service Network</h4>
                    <p className="text-sm text-muted-foreground">Access to verified professionals across 25+ countries</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Icon name="CheckCircle" size={24} className="text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Secure Transactions</h4>
                    <p className="text-sm text-muted-foreground">End-to-end encrypted payment and data protection</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Icon name="CheckCircle" size={24} className="text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Quality Assurance</h4>
                    <p className="text-sm text-muted-foreground">Comprehensive review system and service guarantees</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Icon name="CheckCircle" size={24} className="text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">24/7 Support</h4>
                    <p className="text-sm text-muted-foreground">Round-the-clock customer support in multiple languages</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Certifications & Trust */}
        <section className="py-16 md:py-24 px-4 md:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Certifications & Compliance</h2>
              <p className="text-lg text-muted-foreground">Committed to the highest standards of quality and security</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {certifications?.map((cert, index) =>
              <div key={index} className="bg-card rounded-xl p-6 border border-border text-center hover:shadow-lg transition-shadow">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                    <Icon name="Award" size={32} className="text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{cert?.name}</h3>
                  <p className="text-sm text-muted-foreground">{cert?.description}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Global Presence */}
        <section className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-muted">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Global Presence</h2>
              <p className="text-lg text-muted-foreground">Serving businesses across continents with localized expertise</p>
            </div>
            <div className="bg-card rounded-2xl p-8 md:p-12 border border-border">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                <div className="text-center">
                  <Icon name="MapPin" size={40} className="text-primary mx-auto mb-3" />
                  <h3 className="text-2xl font-bold text-foreground mb-2">25+</h3>
                  <p className="text-muted-foreground">Countries Served</p>
                </div>
                <div className="text-center">
                  <Icon name="Users" size={40} className="text-primary mx-auto mb-3" />
                  <h3 className="text-2xl font-bold text-foreground mb-2">500+</h3>
                  <p className="text-muted-foreground">Verified Partners</p>
                </div>
                <div className="text-center">
                  <Icon name="Languages" size={40} className="text-primary mx-auto mb-3" />
                  <h3 className="text-2xl font-bold text-foreground mb-2">15+</h3>
                  <p className="text-muted-foreground">Languages Supported</p>
                </div>
              </div>
              <p className="text-center text-muted-foreground">
                From North America to Asia-Pacific, Europe to Middle East - we're connecting businesses with trusted service professionals worldwide
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Experience With OBASE ?</h2>
            <p className="text-lg opacity-90 mb-8">Join thousands of businesses worldwide who trust OBASGroup for their service needs

            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild>
                <Link to="/service-catalog-home">
                  <Icon name="Rocket" size={20} className="mr-2" />
                  Get Started
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-white/10" asChild>
                <Link to="/partner-dashboard">
                  <Icon name="Handshake" size={20} className="mr-2" />
                  Become a Partner
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 md:py-24 px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Get in Touch</h2>
              <p className="text-lg text-muted-foreground">We'd love to hear from you</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-card rounded-xl p-6 border border-border text-center">
                <Icon name="Mail" size={32} className="text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-2">Email Us</h3>
                <a href="mailto:business@otassist.in" className="text-sm text-primary hover:underline">business@obasegroup.com

                </a>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border text-center">
                <Icon name="Globe" size={32} className="text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-2">Visit Website</h3>
                <a href="https://obas.in" target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">www.ebas.in<br/>www.eucit.in<br/>&nbsp;www.pickpa.com
                </a>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border text-center">
                <Icon name="Building2" size={32} className="text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-2">Companies</h3>
                <p className="text-sm text-muted-foreground">EBAS Venture Private Limited<br />EUCIT Systems Private Limited<br />PickPA Service Private Limited</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      {/* Footer */}
      <footer className="bg-card border-t border-border py-8 md:py-12 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-8 md:mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/assets/images/160x55-1775850152777.jpg" alt="ZippyAssist Logo" className="h-10 w-auto object-contain" />
                <span className="text-xl font-semibold text-foreground">ZippyAssist</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                The OBASEGroup - Your trusted marketplace for discovering and connecting with verified service professionals globally.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-foreground mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/service-catalog-home" className="text-sm text-muted-foreground hover:text-primary">Home</Link></li>
                <li><Link to="/service-category-browse" className="text-sm text-muted-foreground hover:text-primary">Browse Services</Link></li>
                <li><Link to="/about-ebas-venture" className="text-sm text-muted-foreground hover:text-primary">About Us</Link></li>
                <li><a href="https://obas.in" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-primary">OBASEgroup.com</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-base font-semibold text-foreground mb-4">For Partners</h3>
              <ul className="space-y-2">
                <li><Link to="/partner-dashboard" className="text-sm text-muted-foreground hover:text-primary">Partner Dashboard</Link></li>
                <li><a href="#" className="text-sm text-muted-foreground hover:text-primary">List Your Service</a></li>
                <li><a href="#" className="text-sm text-muted-foreground hover:text-primary">Partner Resources</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-base font-semibold text-foreground mb-4">Contact</h3>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Mail" size={16} />
                  <a href="mailto:business@obasegroup.com" className="hover:text-primary">business@obasegroup.com</a>
                </li>
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Globe" size={16} />
                  <a href="https://www.obasegroup.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary">www.obasegroup.com</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-border text-center">
            <p className="text-sm text-muted-foreground">
              &copy; {new Date()?.getFullYear()} ZippyAssist by OBASEGroup. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>);};
export default AboutEbasVenture;